import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Alert, Card, CardContent
} from '@mui/material';
import HubIcon from '@mui/icons-material/Hub';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { useAppContext } from '../AppContext';
import { ConcepteIcon } from './IconMap';
import { findNodeById, findNodeByPath, findAllNodesByName, findNodeByNameOrCode } from '../../services/concepteGraphService';

/**
 * Banner superior per a dashboards de concepte i nodes jeràrquics.
 * Permet distingir si s'està veient un node concret de graf (ID nou)
 * o un concepte clàssic (ID antic), i oferir canvi entre ells.
 */
export const ConcepteGraphBanner = ({
  concepteNom,
  nodeId = null,
  activeNode = null,
  userapp,
  anyo,
  mes
}) => {
  const navigate = useNavigate();
  const { conceptesTree, entitatsTransversals } = useAppContext();

  // Si tenim un nodeId o activeNode, estem en mode Node de Graf (ID nou)
  const currentNode = useMemo(() => {
    if (activeNode && Array.isArray(activeNode.fills) && activeNode.fills.length > 0) return activeNode;
    const targetId = nodeId || activeNode?.id;
    if (targetId && conceptesTree) {
      const found = findNodeById(conceptesTree, targetId);
      if (found) return { ...found, ...activeNode };
    }
    if (activeNode) return activeNode;
    return findNodeByNameOrCode(conceptesTree, concepteNom);
  }, [activeNode, nodeId, conceptesTree, concepteNom]);

  // Buscar tots els nodes del graf que comparteixen aquest nom (ex: Parking a Transport i Parking a Vacances)
  const matchingNodes = useMemo(() => {
    const searchName = currentNode?.nom || concepteNom;
    return findAllNodesByName(conceptesTree, searchName);
  }, [conceptesTree, currentNode, concepteNom]);

  const isSpecificNodeView = Boolean(nodeId || activeNode);

  const isTransversal = useMemo(() => {
    const searchName = currentNode?.nom || concepteNom;
    const inEntitats = entitatsTransversals.some(
      e => e.nom?.toLowerCase() === searchName?.toLowerCase()
    );
    const inNode = currentNode && (currentNode.tipusNode === 'VEHICLE' || currentNode.tipusNode === 'PERSONA');
    return inEntitats || inNode;
  }, [entitatsTransversals, currentNode, concepteNom]);

  const handleGotoClassicConcepte = (nom) => {
    const targetNom = nom || currentNode?.nom || concepteNom;
    if (anyo && mes) {
      navigate(`/${userapp}/dashboard/${anyo}/mes/${mes}/concepte/${targetNom}`);
    } else if (anyo) {
      navigate(`/${userapp}/dashboard/${anyo}/concepte/${targetNom}`);
    } else {
      navigate(`/${userapp}/dashboard/concepte/${targetNom}`);
    }
  };

  const handleGotoSpecificNode = (targetNodeId, targetNodePath = null) => {
    const p = targetNodePath;
    const pathQuery = p ? `?path=${encodeURIComponent(p)}` : '';
    if (anyo && mes) {
      navigate(`/${userapp}/dashboard/${anyo}/mes/${mes}/node/${targetNodeId}${pathQuery}`);
    } else if (anyo) {
      navigate(`/${userapp}/dashboard/${anyo}/node/${targetNodeId}${pathQuery}`);
    } else {
      navigate(`/${userapp}/dashboard/node/${targetNodeId}${pathQuery}`);
    }
  };

  const handleGotoTransversal = () => {
    if (anyo) {
      navigate(`/${userapp}/transversal/${anyo}`);
    } else {
      navigate(`/${userapp}/transversal`);
    }
  };

  const handleGotoJerarquic = () => {
    if (anyo) {
      navigate(`/${userapp}/jerarquic/${anyo}`);
    } else {
      navigate(`/${userapp}/jerarquic`);
    }
  };

  return (
    <Box sx={{ mt: 1, mb: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* 1. Indicador d'estat del Node si estem veient un node concret de graf */}
      {isSpecificNodeView && currentNode && (
        <Card variant="outlined" sx={{ bgcolor: '#f0f7ff', borderColor: '#90caf9' }}>
          <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <AccountTreeIcon color="primary" fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                  Node de Graf: {currentNode.fullBreadcrumb || currentNode.pathLabel || currentNode.nom}
                </Typography>
                <Chip label={`Nivell ${currentNode.nivell || 1}`} size="small" color="primary" sx={{ height: 20 }} />
                {currentNode.concepteClassic && (
                  <Chip
                    label={`Clàssic: ${currentNode.concepteClassic}`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, bgcolor: '#ffffff', borderColor: '#90caf9', fontWeight: 500 }}
                  />
                )}
              </Box>

              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => handleGotoClassicConcepte(currentNode.concepteClassic || currentNode.nom)}
                sx={{ textTransform: 'none', py: 0.2 }}
              >
                Veure resum clàssic de {currentNode.concepteClassic || currentNode.nom} &rarr;
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* 2. Si estem veient el concepte clàssic i apareix en múltiples nodes del graf */}
      {!isSpecificNodeView && matchingNodes.length > 1 && (
        <Alert
          severity="info"
          icon={<CallSplitIcon />}
          sx={{ py: 0.5, alignItems: 'center' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              Aquest concepte apareix en {matchingNodes.length} branques diferents del graf:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {matchingNodes.map((mn) => (
                <Chip
                  key={mn.id}
                  label={mn.fullBreadcrumb || mn.pathLabel || mn.nom}
                  size="small"
                  clickable
                  color="primary"
                  variant="outlined"
                  onClick={() => handleGotoSpecificNode(mn.id, mn.conceptePath || mn.path)}
                  sx={{ fontSize: '0.70rem' }}
                />
              ))}
            </Box>
          </Box>
        </Alert>
      )}

      {/* 3. Botons auxiliars (Transversal / Arbre) */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        {isTransversal && (
          <Button
            variant="contained"
            size="small"
            color="secondary"
            startIcon={<HubIcon />}
            onClick={handleGotoTransversal}
            sx={{ textTransform: 'none', py: 0.3 }}
          >
            Consultar Resum General Transversal ({concepteNom || currentNode?.nom})
          </Button>
        )}

        <Button
          variant="text"
          size="small"
          startIcon={<AccountTreeIcon />}
          onClick={handleGotoJerarquic}
          sx={{ textTransform: 'none', py: 0.3 }}
        >
          Veure a l'arbre jeràrquic
        </Button>
      </Box>
    </Box>
  );
};

/**
 * Component per mostrar la llista de subelements d'un concepte de Nivell 1 o 2.
 * Permet navegar a cada fill tant pel seu ID nou de node com pel seu nom.
 */
export const SubElementsList = ({
  concepteNom,
  nodeId = null,
  activeNode = null,
  path = null,
  userapp,
  anyo,
  mes
}) => {
  const navigate = useNavigate();
  const { conceptesTree } = useAppContext();

  const currentNode = useMemo(() => {
    // 1. Si activeNode ja té fills poblats, l'utilitzem directament
    if (activeNode && Array.isArray(activeNode.fills) && activeNode.fills.length > 0) {
      return activeNode;
    }
    // 2. Cercar a l'arbre complet pel targetId (nodeId o activeNode.id)
    const targetId = nodeId || activeNode?.id;
    if (targetId && conceptesTree) {
      const found = findNodeById(conceptesTree, targetId);
      if (found) {
        return {
          ...found,
          ...activeNode,
          fills: (found.fills && found.fills.length > 0) ? found.fills : (activeNode?.fills || [])
        };
      }
    }
    // 3. Cercar a l'arbre per path
    const targetPath = path || activeNode?.conceptePath || activeNode?.path;
    if (targetPath && conceptesTree) {
      const foundByPath = findNodeByPath(conceptesTree, targetPath);
      if (foundByPath) {
        return {
          ...foundByPath,
          ...activeNode,
          fills: (foundByPath.fills && foundByPath.fills.length > 0) ? foundByPath.fills : (activeNode?.fills || [])
        };
      }
    }
    // 4. Fallback: cerca per nom o codi
    if (concepteNom && conceptesTree) {
      const foundByName = findNodeByNameOrCode(conceptesTree, concepteNom);
      if (foundByName) {
        return {
          ...foundByName,
          ...activeNode,
          fills: (foundByName.fills && foundByName.fills.length > 0) ? foundByName.fills : (activeNode?.fills || [])
        };
      }
    }
    return activeNode;
  }, [activeNode, nodeId, path, conceptesTree, concepteNom]);

  if (!currentNode || !currentNode.fills || currentNode.fills.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Aquest concepte és un node fulla i no conté subcategories descendents.
        </Typography>
      </Paper>
    );
  }

  const handleGotoChildNode = (child) => {
    // Navegar pel seu ID nou de node per evitar ambigüitats en conceptes compartits
    if (child.id) {
      const p = child.conceptePath || child.path;
      const pathQuery = p ? `?path=${encodeURIComponent(p)}` : '';
      if (anyo && mes) {
        navigate(`/${userapp}/dashboard/${anyo}/mes/${mes}/node/${child.id}${pathQuery}`);
      } else if (anyo) {
        navigate(`/${userapp}/dashboard/${anyo}/node/${child.id}${pathQuery}`);
      } else {
        navigate(`/${userapp}/dashboard/node/${child.id}${pathQuery}`);
      }
    } else {
      // Fallback pel seu nom
      if (anyo && mes) {
        navigate(`/${userapp}/dashboard/${anyo}/mes/${mes}/concepte/${child.nom}`);
      } else if (anyo) {
        navigate(`/${userapp}/dashboard/${anyo}/concepte/${child.nom}`);
      } else {
        navigate(`/${userapp}/dashboard/concepte/${child.nom}`);
      }
    }
  };

  return (
    <TableContainer component={Paper} elevation={1}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell><strong>Subelement / Concepte Filla</strong></TableCell>
            <TableCell><strong>ID Node</strong></TableCell>
            <TableCell><strong>Tipus Node</strong></TableCell>
            <TableCell><strong>Nivell</strong></TableCell>
            <TableCell align="right"><strong>Acció</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {currentNode.fills.map((child) => (
            <TableRow
              key={child.id || child.codi}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => handleGotoChildNode(child)}
            >
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ConcepteIcon
                    code={child.codi}
                    concept={child.nom}
                    fallback={currentNode?.nom || child.concepteClassic}
                    tipusNode={child.tipusNode}
                    level={child.nivell || 2}
                    parentConcept={currentNode?.nom}
                    onlyIcon
                    sx={{ fontSize: 18 }}
                  />
                  <Typography variant="body2" fontWeight={500}>
                    {child.nom}
                  </Typography>
                </Box>
              </TableCell>

              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                  #{child.id}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {child.isLeaf ? 'Fulla' : 'Subcategoria'}
                </Typography>
              </TableCell>

              <TableCell>
                <Chip
                  label={child.tipusNode || 'CATEGORIA'}
                  size="small"
                  sx={{
                    fontSize: '0.65rem',
                    height: 18,
                    bgcolor: child.tipusNode === 'VEHICLE' ? '#e1f5fe' : child.tipusNode === 'PERSONA' ? '#f3e5f5' : '#ede7f6'
                  }}
                />
              </TableCell>

              <TableCell>
                <Chip label={`N${child.nivell || 2}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
              </TableCell>

              <TableCell align="right">
                <Button size="small" onClick={(e) => { e.stopPropagation(); handleGotoChildNode(child); }}>
                  Veure Detall &rarr;
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
